/**
 * useAuthStore — backward-compatibility facade over the split stores.
 *
 * As of architecture review item 4, the monolithic auth store has been split
 * into three focused Pinia stores:
 *
 *   • `useSessionStore`       JWT/session lifecycle, `isAuthenticated`,
 *                             `getUserInformation`, `logoutUser`.
 *   • `useUserStore`          Profile fields (id, email, names, tenant,
 *                             visitor id, route change telemetry).
 *   • `useEntitlementsStore`  Permissions, super-user flag, entitlements,
 *                             subscription state.
 *
 * Existing consumers continue to import `useAuthStore` and access the same
 * keys/actions as before — this file proxies reads/writes/actions to the
 * appropriate sub-store via a setup-style Pinia definition. New code should
 * import the focused stores directly.
 */

import { computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useSessionStore } from './useSessionStore';
import { useUserStore } from './useUserStore';
import { useEntitlementsStore } from './useEntitlementsStore';

export const useAuthStore = defineStore('auth', () => {
  const sessionStore = useSessionStore();
  const userStore = useUserStore();
  const entitlementsStore = useEntitlementsStore();

  const sessionRefs = storeToRefs(sessionStore);
  const userRefs = storeToRefs(userStore);
  const entitlementsRefs = storeToRefs(entitlementsStore);

  // setUserData wrote across all three stores in the legacy module; preserve
  // that surface for the few remaining direct callers.
  function setUserData(payload) {
    sessionStore.applyUserPayload(payload);
  }

  // Combined hasPermission getter — uses entitlements + user.
  const hasPermission = computed(() => entitlementsStore.hasPermission);
  const hasActiveEntitlements = computed(() => entitlementsStore.hasActiveEntitlements);
  const visitorIdGetter = computed(() => userStore.visitorIdGetter);
  const getRefreshToken = computed(() => sessionStore.getRefreshToken);

  return {
    // Session refs
    ...sessionRefs,
    // User refs
    ...userRefs,
    // Entitlements refs
    ...entitlementsRefs,

    // Session actions
    clearTokens: sessionStore.clearTokens,
    setAccessToken: sessionStore.setAccessToken,
    setRefreshToken: sessionStore.setRefreshToken,
    setIsAuthenticated: sessionStore.setIsAuthenticated,
    setFetchingUser: sessionStore.setFetchingUser,
    setRefreshingUser: sessionStore.setRefreshingUser,
    setUserLoaded: sessionStore.setUserLoaded,
    getUserInformation: (...args) => sessionStore.getUserInformation(...args),
    hydrateCachedUser: () => sessionStore.hydrateCachedUser(),
    logoutUser: () => sessionStore.logoutUser(),
    refreshToken: () => sessionStore.refreshToken(),
    clearUserData: () => sessionStore.clearUserData(),

    // User actions
    setTenant: userStore.setTenant,
    setVisitorId: userStore.setVisitorId,
    setPhoneNumber: userStore.setPhoneNumber,
    setHasVerifiedPhoneNumber: userStore.setHasVerifiedPhoneNumber,
    user: userStore.user,

    // Cross-store helpers
    setUserData,
    fetchAccess: () => entitlementsStore.fetchAccess(),
    refreshAccessIfStale: () => entitlementsStore.refreshAccessIfStale(),
    syncAccessVersion: (...args) => entitlementsStore.syncAccessVersion(...args),

    // Getters
    hasPermission,
    hasActiveEntitlements,
    visitorIdGetter,
    getRefreshToken,
  };
});
